interface AddressComponentElements {
  addressLine1: HTMLInputElement;
  addressLine2: HTMLInputElement;
  city: HTMLInputElement;
  regionText: HTMLInputElement;
  regionSelect: HTMLSelectElement;
  postalCode: HTMLInputElement;
  country: HTMLSelectElement;
}

class AddressAutocomplete {
  private sessionToken: google.maps.places.AutocompleteSessionToken | undefined;
  private currentSuggestions: google.maps.places.AutocompleteSuggestion[] = [];
  private activeSuggestionIndex = -1;

  private countryRegions = new Map<string, Map<string, string>>([
    ['Canada', new Map<string, string>([
      ['AB', 'Alberta'],
      ['BC', 'British Columbia'],
      ['MB', 'Manitoba'],
      ['NB', 'New Brunswick'],
      ['NL', 'Newfoundland and Labrador'],
      ['NS', 'Nova Scotia'],
      ['NT', 'Northwest Territories'],
      ['NU', 'Nunavut'],
      ['ON', 'Ontario'],
      ['PE', 'Prince Edward Island'],
      ['QC', 'Quebec'],
      ['SK', 'Saskatchewan'],
      ['YT', 'Yukon'],
    ])],
    ['United States', new Map<string, string>([
      ['AL', 'Alabama'],
      ['AK', 'Alaska'],
      ['AZ', 'Arizona'],
      ['AR', 'Arkansas'],
      ['CA', 'California'],
      ['CO', 'Colorado'],
      ['CT', 'Connecticut'],
      ['DE', 'Delaware'],
      ['DC', 'District of Columbia'],
      ['FL', 'Florida'],
      ['GA', 'Georgia'],
      ['GU', 'Guam'],
      ['HI', 'Hawaii'],
      ['ID', 'Idaho'],
      ['IL', 'Illinois'],
      ['IN', 'Indiana'],
      ['IA', 'Iowa'],
      ['KS', 'Kansas'],
      ['KY', 'Kentucky'],
      ['LA', 'Louisiana'],
      ['ME', 'Maine'],
      ['MH', 'Marshall Islands'],
      ['MD', 'Maryland'],
      ['MA', 'Massachusetts'],
      ['MI', 'Michigan'],
      ['MN', 'Minnesota'],
      ['MS', 'Mississippi'],
      ['MO', 'Missouri'],
      ['MT', 'Montana'],
      ['NE', 'Nebraska'],
      ['NV', 'Nevada'],
      ['NH', 'New Hampshire'],
      ['NJ', 'New Jersey'],
      ['NM', 'New Mexico'],
      ['NY', 'New York'],
      ['NC', 'North Carolina'],
      ['ND', 'North Dakota'],
      ['MP', 'Northern Mariana Island'],
      ['OH', 'Ohio'],
      ['OK', 'Oklahoma'],
      ['OR', 'Oregon'],
      ['PA', 'Pennsylvania'],
      ['PR', 'Puerto Rico'],
      ['RI', 'Rhode Island'],
      ['SC', 'South Carolina'],
      ['SD', 'South Dakota'],
      ['TN', 'Tennessee'],
      ['TX', 'Texas'],
      ['UT', 'Utah'],
      ['VT', 'Vermont'],
      ['VI', 'Virgin Islands'],
      ['VA', 'Virginia'],
      ['WA', 'Washington'],
      ['WV', 'West Virginia'],
      ['WI', 'Wisconsin'],
      ['WY', 'Wyoming'],
    ])],
  ]);

  public constructor(formId: number, elAddressAutocomplete: HTMLElement) {
    // hide the component address fields
    elAddressAutocomplete.dataset['showComponentFields'] = 'false';

    // identify all form fields
    let els: AddressComponentElements;
    try {
      els = this.getComponentFields(formId, elAddressAutocomplete);
    } catch {
      return;
    }

    // set visibility of extended address fields - defaults to hidden, but may have loaded with values when form submitted but validation failed
    //this.toggleAddressFieldsVisibility();
    elAddressAutocomplete.dataset['showComponentFields'] = String(this.isExtendedAddressData(els));

    (async () => {
      // Wait for the Google Places API to become available. Use window.google to avoid
      // ReferenceError when the global hasn't been initialized yet.
      const hasPlacesApi = (): boolean => {
        const g = (window as typeof window & { google?: typeof google }).google;
        return !!g?.maps?.places;
      };

      let attempts = 0;
      while (!hasPlacesApi()) {
        attempts++;
        if (attempts > 40) {
          return;
        }
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      const g = (window as typeof window & { google: typeof google }).google;

      // dropdown of address suggestions, shown below addressLine1 as the user types
      const elSuggestions = document.createElement('ul');
      elSuggestions.className = 'sh_address-autocomplete-suggestions';
      elSuggestions.setAttribute('role', 'listbox');
      elSuggestions.dataset['visible'] = 'false';
      els.addressLine1.insertAdjacentElement('afterend', elSuggestions);

      let debounceTimer: ReturnType<typeof setTimeout> | undefined;
      els.addressLine1.addEventListener('input', () => {
        if (debounceTimer !== undefined) {
          clearTimeout(debounceTimer);
        }
        debounceTimer = setTimeout(() => {
          void this.fetchSuggestions(g, els.addressLine1.value, elSuggestions, els, elAddressAutocomplete);
        }, 300);
      });

      // when selected country changed
      els.country?.addEventListener('change', (ev) => {
        if (ev.currentTarget instanceof HTMLSelectElement) { // superfluous type safety to placate TypeScript - Event.currentTarget can only be HTMLSelectElement
          // the select's value is the ISO country code; countryRegions is keyed by full country name, so use the option's label instead
          this.onCountrySelectChanged(ev.currentTarget.selectedOptions[0]?.text ?? '', els);
        }
      });
      // when selected region changed - copy selected regio into regionText
      els.regionSelect?.addEventListener('change', (ev) => { // superfluous type safety to placate TypeScript - Event.currentTarget can only be HTMLSelectElement
        if (ev.currentTarget instanceof HTMLSelectElement) {
          els.regionText.value = ev.currentTarget?.value;
        }
      });

      // keyboard navigation of the suggestions list; also prevent 'Enter' from submitting the form
      els.addressLine1?.addEventListener('keydown', (ev) => {
        if (ev.key === 'ArrowDown') {
          ev.preventDefault();
          this.moveActiveSuggestion(1, elSuggestions);
        } else if (ev.key === 'ArrowUp') {
          ev.preventDefault();
          this.moveActiveSuggestion(-1, elSuggestions);
        } else if (ev.key === 'Escape') {
          this.hideSuggestions(elSuggestions);
        } else if (ev.key === 'Enter') {
          ev.stopPropagation();
          ev.preventDefault();
          const activeSuggestion = this.currentSuggestions[this.activeSuggestionIndex];
          if (activeSuggestion) {
            void this.selectSuggestion(activeSuggestion, els, elAddressAutocomplete, elSuggestions);
          }
        }
      });
      const onAddressLine1Blur = () => {
        // show component address fields
        elAddressAutocomplete.dataset['showComponentFields'] = 'true';
        els.addressLine1?.removeEventListener('blur', onAddressLine1Blur); // event binding is only needed the first time this field loses focus
      };
      // 'mousedown' on a suggestion (see renderSuggestions) preventDefault()s so this blur only fires for genuine focus loss
      els.addressLine1?.addEventListener('blur', () => this.hideSuggestions(elSuggestions));
      els.addressLine1?.addEventListener('blur', onAddressLine1Blur);
    })();
  }

  /**
   * Fetch address suggestions for the current input value and render them in the dropdown
   */
  private async fetchSuggestions(g: typeof google, input: string, elSuggestions: HTMLUListElement, els: AddressComponentElements, elAddressAutocomplete: HTMLElement): Promise<void> {
    const trimmedInput = input.trim();
    if (!trimmedInput) {
      this.hideSuggestions(elSuggestions);
      return;
    }

    this.sessionToken ??= new g.maps.places.AutocompleteSessionToken();

    const { suggestions } = await g.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: trimmedInput,
      includedPrimaryTypes: ['street_address', 'premise', 'subpremise', 'route'],
      includedRegionCodes: ['us', 'ca', 'gb', 'ae'], // countries the company operates in
      sessionToken: this.sessionToken,
    });

    this.currentSuggestions = suggestions;
    this.activeSuggestionIndex = -1;
    this.renderSuggestions(suggestions, elSuggestions, els, elAddressAutocomplete);
  }

  /**
   * Populate the suggestions dropdown with the given list of suggestions
   */
  private renderSuggestions(suggestions: google.maps.places.AutocompleteSuggestion[], elSuggestions: HTMLUListElement, els: AddressComponentElements, elAddressAutocomplete: HTMLElement): void {
    elSuggestions.replaceChildren();

    if (!suggestions.length) {
      this.hideSuggestions(elSuggestions);
      return;
    }

    for (const suggestion of suggestions) {
      const placePrediction = suggestion.placePrediction;
      if (!placePrediction) {
        continue;
      }

      const elItem = document.createElement('li');
      elItem.setAttribute('role', 'option');
      elItem.textContent = placePrediction.text.toString();
      // use 'mousedown' with preventDefault() so addressLine1 doesn't blur/hide the list before the selection is handled
      elItem.addEventListener('mousedown', (ev) => {
        ev.preventDefault();
        void this.selectSuggestion(suggestion, els, elAddressAutocomplete, elSuggestions);
      });
      elSuggestions.append(elItem);
    }

    elSuggestions.dataset['visible'] = 'true';
  }

  /**
   * Move the keyboard-active suggestion up/down by `delta`, wrapping around the ends of the list
   */
  private moveActiveSuggestion(delta: number, elSuggestions: HTMLUListElement): void {
    const items = elSuggestions.querySelectorAll('li');
    if (!items.length) {
      return;
    }

    this.activeSuggestionIndex = (this.activeSuggestionIndex + delta + items.length) % items.length;

    items.forEach((elItem, index) => {
      elItem.dataset['active'] = String(index === this.activeSuggestionIndex);
      elItem.setAttribute('aria-selected', String(index === this.activeSuggestionIndex));
    });
    items[this.activeSuggestionIndex]?.scrollIntoView({ block: 'nearest' });
  }

  /**
   *
   */
  private hideSuggestions(elSuggestions: HTMLUListElement): void {
    elSuggestions.replaceChildren();
    elSuggestions.dataset['visible'] = 'false';
    this.currentSuggestions = [];
    this.activeSuggestionIndex = -1;
  }

  /**
   * Fetch full place details for the selected suggestion and populate the address fields
   */
  private async selectSuggestion(suggestion: google.maps.places.AutocompleteSuggestion, els: AddressComponentElements, elAddressAutocomplete: HTMLElement, elSuggestions: HTMLUListElement): Promise<void> {
    const placePrediction = suggestion.placePrediction;
    if (!placePrediction) {
      return;
    }

    const place = placePrediction.toPlace();
    await place.fetchFields({ fields: ['addressComponents'] });
    // fetchFields() concludes the autocomplete session; a fresh token is generated for the next one
    this.sessionToken = undefined;

    this.onPlaceChanged(place.addressComponents ?? [], els);
    elAddressAutocomplete.dataset['showComponentFields'] = 'true';
    this.hideSuggestions(elSuggestions);
  }

  /**
   * @throws {Error} if can't identify all of the necessary HTML elements
   */
  private getComponentFields(formId: number, elAddressAutocomplete: HTMLElement): AddressComponentElements {
    // get ID of address field
    const addressId = elAddressAutocomplete.getAttribute('id') ?? '';
    // identify inputs: input_{form ID}_{field ID}_{input ID}
    const [, fieldId] = addressId.match(/^field_[0-9]+_([0-9]+)$/) ?? [];

    const elAddressLine1 = document.getElementById(`input_${formId}_${fieldId}_1`);
    const elAddressLine2 = document.getElementById(`input_${formId}_${fieldId}_2`);
    const elCity = document.getElementById(`input_${formId}_${fieldId}_3`);
    const elRegionText = document.getElementById(`input_${formId}_${fieldId}_4`);

    // create region <select>
    const elRegionSelect = document.createElement('select');
    elRegionSelect.setAttribute('name', 'regionSelect');
    elRegionSelect.dataset['autocompleteRegionVisible'] = 'false'; // hide region select by default
    elRegionText?.insertAdjacentElement('afterend', elRegionSelect);

    const elPostalCode = document.getElementById(`input_${formId}_${fieldId}_5`);
    const elCountry = document.getElementById(`input_${formId}_${fieldId}_6`);

    const els = {
      addressLine1: elAddressLine1 instanceof HTMLInputElement ? elAddressLine1 : null,
      addressLine2: elAddressLine2 instanceof HTMLInputElement ? elAddressLine2 : null,
      city: elCity instanceof HTMLInputElement ? elCity : null,
      regionText: elRegionText instanceof HTMLInputElement ? elRegionText : null,
      regionSelect: elRegionSelect instanceof HTMLSelectElement ? elRegionSelect : null,
      postalCode: elPostalCode instanceof HTMLInputElement ? elPostalCode : null,
      country: elCountry instanceof HTMLSelectElement ? elCountry : null,
    };

    if (Object.values(els).some(el => el === null)) {
      throw new Error('Could not find all required elements for Address Autocomplete.');
    }

    return els as AddressComponentElements;
  }

  /**
   * Create all the <option> elements that populate the region <select>
   */
  private populateCountryRegions(elRegionSelect: HTMLSelectElement, regions: Map<string, string> | undefined): void {
    // remove all <option>'s from the region <select>
    if (elRegionSelect?.options.length !== undefined) {
      for (let i = elRegionSelect?.options.length; i >= 0; i--) {
        elRegionSelect?.remove(i);
      }
    }
    // populate region <select> with new <option>'s for the selected country's regions
    for (const [, regionName] of (regions ?? new Map())) {
      const elOption = document.createElement('option');
      elOption.setAttribute('value', regionName); // send full name, not abbreviation
      elOption.innerHTML = regionName;
      elRegionSelect?.add(elOption);
    }
  }

  /**
   *
   */
  private onPlaceChanged(addressComponents: google.maps.places.AddressComponent[], els: AddressComponentElements): void {
    // map values returned by Google Place API to equivalent form field values
    const address = {
      subpremise: '',
      street_number: '',
      route: '',
      sublocality_level_1: '',
      sublocality_level_2: '',
      locality: '',
      administrative_area_level_1: '',
      postal_code: '',
      country: '',
    };

    for (const componentName of Object.keys(address) as (keyof typeof address)[]) {
      address[componentName] = addressComponents.find(component => component.types.includes(componentName))?.longText ?? '';
    }

    let addressLine1 = address.subpremise ? address.subpremise + '/' : '';
    addressLine1 += [address.street_number, address.route].join(' ');
    addressLine1 = addressLine1.trim();
    const addressLine2 = [address.sublocality_level_1, address.sublocality_level_2].join(' ')?.trim();
    const city = address.locality?.trim();
    const region = address.administrative_area_level_1?.trim();
    const postalCode = address.postal_code?.trim();
    const country = address.country?.trim();
    // GF's country <select> is keyed by ISO code, not the full country name
    const countryCode = addressComponents.find(component => component.types.includes('country'))?.shortText?.trim() ?? '';

    // populate the region <select> before setting its value
    const regions = this.countryRegions.get(country);
    this.populateCountryRegions(els.regionSelect, regions);

    // update all address fields
    els.addressLine1.value = addressLine1.trim();
    els.addressLine2.value = addressLine2.trim();
    els.city.value = city.trim();
    els.regionText.value = region;
    els.regionSelect.value = region;
    els.postalCode.value = postalCode.trim();
    this.setCountrySelectValue(els.country, countryCode, country);

    // show/hide the appropriate region field
    els.regionText.dataset['autocompleteRegionVisible'] = String(!regions);
    els.regionSelect.dataset['autocompleteRegionVisible'] = String(!!regions);
  }

  /**
   * Select the <option> matching countryCode (GF's <select> values are ISO codes), falling back to matching countryName against the option label
   */
  private setCountrySelectValue(elCountry: HTMLSelectElement, countryCode: string, countryName: string): void {
    const normalizedCode = countryCode.trim().toLowerCase();
    const normalizedName = countryName.trim().toLowerCase();
    const matchingOption = [...elCountry.options].find(option => option.value.trim().toLowerCase() === normalizedCode || option.textContent?.trim().toLowerCase() === normalizedName);
    elCountry.value = matchingOption ? matchingOption.value : '';
  }

  /**
   *
   */
  private onCountrySelectChanged(countryName: string, els: AddressComponentElements): void {
    const regions = this.countryRegions.get(countryName);
    this.populateCountryRegions(els.regionSelect, regions);

    // When switching to a country with a list of regions, try to use whatever value the user may have manually
    // typed into the region field to set as the selected item in the region drop-down.
    if (regions) {
      // check if regionText value matches any regions in the selected country's regions list
      // check if regionText value matches any regions in the selected country's regions list
      // - check full names, and if no match, check abbreviations
      // - if any match found, use the region name, not the abbreviation, regardless of user input
      const oldRegion = els.regionText.value.trim();
      const regionMatch = [...regions.values()].find(regionName => regionName.toLowerCase() === oldRegion.toLowerCase()) ?? regions.get(oldRegion.toUpperCase());

      els.regionText.value = '';
      els.regionSelect.value = '';
      if (regionMatch) {
        els.regionText.value = regionMatch;
        els.regionSelect.value = regionMatch;
      }
    } else {
      // clear the region value
      els.regionText.value = '';
      els.regionSelect.value = '';
    }

    // show/hide the selected country's corresponding region field (<select> or <input>)
    els.regionText.dataset['autocompleteRegionVisible'] = String(!regions);
    els.regionSelect.dataset['autocompleteRegionVisible'] = String(!!regions);
  }

  /**
   * Determine if there's values set in any of the component elements of the address
   */
  private isExtendedAddressData(els: AddressComponentElements): boolean {
    return !!([
      els.addressLine1?.value.trim(),
      els.addressLine2?.value.trim(),
      els.city?.value.trim(),
      els.regionText?.value.trim(),
      els.postalCode?.value.trim(),
      els.country?.value.trim(),
    ].filter(value => !!value).length);
  }
}

jQuery(document).on('gform_post_render', (_ev: JQuery.Event, formId: number) => {
  const elAddressAutocomplete = document.getElementById(`gform_${formId}`)?.querySelector('.sh_form-address-autocomplete');
  if (elAddressAutocomplete instanceof HTMLElement) {
    new AddressAutocomplete(formId, elAddressAutocomplete);
  }
});
